import { api } from "./api";
import { getEnv } from "@/config/env";

const API_BASE_URL = getEnv("VITE_API_BASE_URL") || "http://localhost:8000";
const CONTENT_INTEL_URL = getEnv("VITE_CONTENT_INTELLIGENCE_API_URL") || "http://localhost:8001";

let tokenGetter: (() => Promise<string | null>) | null = null;
export function setChatStreamTokenGetter(fn: () => Promise<string | null>) {
  tokenGetter = fn;
}

// ── API response shapes (snake_case from backend) ────────────────────────────

interface ChatSessionApiResponse {
  id: string;
  project_id: string;
  created_by_user_id: string | null;
  created_by_email: string | null;
  title: string;
  metadata_filters: Record<string, string[]> | null;
  web_search_enabled: boolean;
  // True once any message in the session has actually completed a web
  // search — distinct from web_search_enabled, which is just the per-session
  // opt-in toggle and can be on without a search ever having run.
  has_web_search: boolean;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  is_owner: boolean;
}

export interface ChatCitation {
  id: string;
  content: string;
  sourcefile: string;
  sourcepage: string;
}

// Self-reported by the model as an inline markdown link — [id](url) — and
// parsed back out server-side, since Anthropic's native per-span web
// citations weren't reliably populated in practice. Same [N] numbering
// convention as document citations, resolved to a URL instead of a
// document/page.
export interface WebCitation {
  id: string;
  url: string;
}

export type WebSearchStatus = "ok" | "not_needed" | "error";

// "interrupted" means this turn's stream was cut off — the client
// disconnected (refresh, tab close) or the user pressed Stop — so `content`
// holds a partial answer the backend persisted from its teardown path. Null
// means the turn completed, which is also true of every pre-migration row.
export type MessageStatus = "interrupted";

// One filter selection in effect at send-time. `label` is snapshotted so
// the chat history footer renders correctly after a custom field rename.
export interface AppliedFilterItem {
  key: string;
  label: string;
  values: string[];
}

// Self-contained snapshot persisted on the assistant message. Empty `items`
// means "no filters applied" (use `null` instead in that case to skip render).
export interface AppliedFilters {
  items: AppliedFilterItem[];
  matchedCount: number;
  totalCount: number;
}

interface AppliedFiltersApiResponse {
  items: AppliedFilterItem[];
  matched_count: number;
  total_count: number;
}

function toAppliedFilters(r: AppliedFiltersApiResponse | null | undefined): AppliedFilters | null {
  if (!r) return null;
  return {
    items: r.items ?? [],
    matchedCount: r.matched_count,
    totalCount: r.total_count,
  };
}

function fromAppliedFilters(f: AppliedFilters | null | undefined): AppliedFiltersApiResponse | null {
  if (!f) return null;
  return {
    items: f.items,
    matched_count: f.matchedCount,
    total_count: f.totalCount,
  };
}

interface ChatMessageApiResponse {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sender_user_id: string | null;
  sender_email: string | null;
  citations: ChatCitation[] | null;
  applied_filters: AppliedFiltersApiResponse | null;
  thinking: string | null;
  web_citations: WebCitation[] | null;
  web_search_status: WebSearchStatus | null;
  status: MessageStatus | null;
  created_at: string;
}

interface PaginatedMessagesApiResponse {
  items: ChatMessageApiResponse[];
  next_cursor: string | null;
}

interface SendMessageApiResponse {
  user_message: ChatMessageApiResponse;
  assistant_message: ChatMessageApiResponse;
}

// ── Camel-case views ─────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  projectId: string;
  createdByUserId: string | null;
  createdByEmail: string | null;
  title: string;
  // Saved filter selection for this session: { filterKey: [value, ...] }.
  // Null means "no filters applied" — chat searches every active project doc.
  metadataFilters: Record<string, string[]> | null;
  // Per-session opt-in for native Anthropic web search. Off by default —
  // toggling it makes web search available for future turns, it doesn't
  // force one (see ChatMessage.webSearchStatus).
  webSearchEnabled: boolean;
  // True once at least one message in the session actually completed a web
  // search (see ChatMessage.webSearchStatus === "ok"). Drives the sidebar's
  // web-search indicator.
  hasWebSearch: boolean;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  isOwner: boolean;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant" | "system";
  content: string;
  senderUserId: string | null;
  senderEmail: string | null;
  citations: ChatCitation[] | null;
  // Snapshot of the filter selection that was in effect when this turn was
  // generated. Set on assistant rows; null on user rows and on legacy data.
  appliedFilters: AppliedFilters | null;
  // Claude's extended-thinking trace for this turn, when available. Null on
  // user rows and on assistant rows sent via the non-streaming send path.
  thinking: string | null;
  // Present only when webSearchStatus is "ok" — in that case `content`
  // above *is* the web-search answer (RAG was skipped for this turn) and
  // these are its citations. Mutually exclusive with `citations`: a turn's
  // content comes from either documents (citations set) or the web
  // (webCitations set), never both.
  webCitations: WebCitation[] | null;
  // "ok" (content is a web-search answer), "not_needed" (web search was on
  // but the model decided this question didn't need one, so content is a
  // normal document-grounded answer), "error" (search attempted but
  // failed, content falls back to documents), or null (web search was off
  // for this turn).
  webSearchStatus: WebSearchStatus | null;
  // Non-null only on a truncated assistant turn — see MessageStatus.
  status: MessageStatus | null;
  createdAt: string;
  // Retrieval route used for this turn: "graph" | "grep" | "rag" | "full".
  // Captured from the SSE "routing" status event and embedded client-side;
  // not persisted by the backend so null on historical messages.
  route: string | null;
}

export interface PaginatedMessages {
  items: ChatMessage[];
  nextCursor: string | null;
}

export interface SendMessageResult {
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
}

function toSession(r: ChatSessionApiResponse): ChatSession {
  return {
    id: r.id,
    projectId: r.project_id,
    createdByUserId: r.created_by_user_id,
    createdByEmail: r.created_by_email,
    title: r.title,
    metadataFilters: r.metadata_filters,
    webSearchEnabled: r.web_search_enabled,
    hasWebSearch: r.has_web_search,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    lastMessageAt: r.last_message_at,
    isOwner: r.is_owner,
  };
}

function toMessage(r: ChatMessageApiResponse): ChatMessage {
  return {
    id: r.id,
    sessionId: r.session_id,
    role: r.role,
    content: r.content,
    senderUserId: r.sender_user_id,
    senderEmail: r.sender_email,
    citations: r.citations,
    appliedFilters: toAppliedFilters(r.applied_filters),
    thinking: r.thinking,
    webCitations: r.web_citations,
    webSearchStatus: r.web_search_status,
    status: r.status ?? null,
    createdAt: r.created_at,
    route: null,
  };
}

export const chatApi = {
  listSessions: async (projectId: string): Promise<ChatSession[]> => {
    const data = await api.get<ChatSessionApiResponse[]>(
      `/api/projects/${projectId}/chat-sessions`,
    );
    return data.map(toSession);
  },

  createSession: async (
    projectId: string,
    title?: string,
  ): Promise<ChatSession> => {
    const data = await api.post<ChatSessionApiResponse>(
      `/api/projects/${projectId}/chat-sessions`,
      { title: title ?? null },
    );
    return toSession(data);
  },

  renameSession: async (sessionId: string, title: string): Promise<ChatSession> => {
    const data = await api.patch<ChatSessionApiResponse>(
      `/api/chat-sessions/${sessionId}`,
      { title },
    );
    return toSession(data);
  },

  updateSessionFilters: async (
    sessionId: string,
    metadataFilters: Record<string, string[]> | null,
  ): Promise<ChatSession> => {
    const data = await api.patch<ChatSessionApiResponse>(
      `/api/chat-sessions/${sessionId}/metadata-filters`,
      { metadata_filters: metadataFilters },
    );
    return toSession(data);
  },

  updateSessionWebSearch: async (
    sessionId: string,
    webSearchEnabled: boolean,
  ): Promise<ChatSession> => {
    const data = await api.patch<ChatSessionApiResponse>(
      `/api/chat-sessions/${sessionId}/web-search`,
      { web_search_enabled: webSearchEnabled },
    );
    return toSession(data);
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await api.delete(`/api/chat-sessions/${sessionId}`);
  },

  listMessages: async (
    sessionId: string,
    opts: { cursor?: string | null; limit?: number } = {},
  ): Promise<PaginatedMessages> => {
    const params = new URLSearchParams();
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const data = await api.get<PaginatedMessagesApiResponse>(
      `/api/chat-sessions/${sessionId}/messages${qs ? `?${qs}` : ""}`,
    );
    return {
      items: data.items.map(toMessage),
      nextCursor: data.next_cursor,
    };
  },

  sendMessage: async (
    sessionId: string,
    content: string,
    documentIds?: string[],
    appliedFilters?: AppliedFilters | null,
    isAdvancedRag?: boolean,
  ): Promise<SendMessageResult> => {
    const body: Record<string, unknown> = { content };
    if (documentIds) body.document_ids = documentIds;
    if (appliedFilters) body.applied_filters = fromAppliedFilters(appliedFilters);
    body.is_advanced_rag = isAdvancedRag ?? false;
    const data = await api.post<SendMessageApiResponse>(
      `/api/chat-sessions/${sessionId}/messages`,
      body,
    );
    return {
      userMessage: toMessage(data.user_message),
      assistantMessage: toMessage(data.assistant_message),
    };
  },

  streamMessage: async (
    sessionId: string,
    content: string,
    handlers: ChatStreamHandlers,
    options: {
      documentIds?: string[];
      appliedFilters?: AppliedFilters | null;
      signal?: AbortSignal;
      isAdvancedRag?: boolean;
    } = {},
  ): Promise<void> => {
    const token = tokenGetter ? await tokenGetter() : null;
    if (!token) throw new Error("Not authenticated");

    const body: Record<string, unknown> = { content };
    if (options.documentIds) body.document_ids = options.documentIds;
    if (options.appliedFilters)
      body.applied_filters = fromAppliedFilters(options.appliedFilters);
    body.is_advanced_rag = options.isAdvancedRag ?? false;
    const response = await fetch(
      `${API_BASE_URL}/api/chat-sessions/${sessionId}/messages/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify(body),
        signal: options.signal,
      },
    );

    if (!response.ok || !response.body) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.detail || `API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line ("\n\n").
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        sep = buffer.indexOf("\n\n");

        for (const line of rawEvent.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const data = line.slice("data:".length).trim();
          if (!data) continue;
          if (data === "[DONE]") {
            handlers.onDone?.();
            return;
          }
          let parsed: ChatStreamEvent;
          try {
            parsed = JSON.parse(data) as ChatStreamEvent;
          } catch {
            continue;
          }
          dispatchStreamEvent(parsed, handlers);
        }
      }
    }
    handlers.onDone?.();
  },
};

// ── Streaming event types ────────────────────────────────────────────────────

// Web-sourced and document-sourced turns are mutually exclusive per message
// (see ChatMessage.webSearchStatus) and never stream concurrently, so both
// share the same "citations"/"delta" event vocabulary — the final
// "assistant_message" event's webSearchStatus/webCitations tell the client
// which source this turn's stream actually was after the fact.
type ChatStreamEvent =
  | { type: "user_message"; message: ChatMessageApiResponse }
  | { type: "citations"; items: ChatCitation[] | WebCitation[] }
  | { type: "thinking"; content: string }
  | { type: "delta"; content: string }
  | { type: "assistant_message"; message: ChatMessageApiResponse }
  | { type: "session_update"; session: { id: string; title: string } }
  | { type: "status"; data: { phase: string; route?: string; message?: string } }
  | { type: "error"; detail: string; status?: number };

export interface ChatStreamHandlers {
  onUserMessage?: (message: ChatMessage) => void;
  onCitations?: (items: ChatCitation[] | WebCitation[]) => void;
  onThinking?: (text: string) => void;
  onDelta?: (text: string) => void;
  onAssistantMessage?: (message: ChatMessage) => void;
  onSessionUpdate?: (update: { id: string; title: string }) => void;
  onStatus?: (data: { phase: string; route?: string }) => void;
  onError?: (detail: string, status?: number) => void;
  onDone?: () => void;
}

function dispatchStreamEvent(event: ChatStreamEvent, h: ChatStreamHandlers) {
  switch (event.type) {
    case "user_message":
      h.onUserMessage?.(toMessage(event.message));
      return;
    case "citations":
      h.onCitations?.(event.items);
      return;
    case "thinking":
      h.onThinking?.(event.content);
      return;
    case "delta":
      h.onDelta?.(event.content);
      return;
    case "assistant_message":
      h.onAssistantMessage?.(toMessage(event.message));
      return;
    case "session_update":
      h.onSessionUpdate?.(event.session);
      return;
    case "status":
      h.onStatus?.(event.data);
      return;
    case "error":
      h.onError?.(event.detail, event.status);
      return;
  }
}
